<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getButtonIcon } from '../constants/buttonTypes';

const props = defineProps({
  bodyMessage: {
    type: String,
    default: '',
  },
  previewPlaceholder: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    required: true,
  },
  buttons: {
    type: Array,
    default: () => [],
  },
  buttonType: {
    type: String,
    default: 'quick_reply',
    validator: value => ['quick_reply', 'call_to_action'].includes(value),
  },
  mediaUrl: {
    type: String,
    default: null,
  },
  mediaType: {
    type: String,
    default: null,
    validator: value =>
      !value || ['image', 'video', 'document'].includes(value),
  },
  footerText: {
    type: String,
    default: '',
  },
  isFlow: {
    type: Boolean,
    default: false,
  },
  flowData: {
    type: Object,
    default: null,
  },
  i18nPrefix: {
    type: String,
    default: 'CONTENT_TEMPLATES',
  },
  buttonTitleField: {
    type: String,
    default: 'title',
  },
});

const { t } = useI18n();

const mediaLoadError = ref(false);
const currentPageIndex = ref(0);

const handleMediaError = () => {
  mediaLoadError.value = true;
};

// Flow preview computed properties
const currentPage = computed(() => {
  if (!props.isFlow || !props.flowData?.pages?.length) return null;
  if (currentPageIndex.value === 0) return null;
  return props.flowData.pages[currentPageIndex.value - 1];
});

const totalPages = computed(() => {
  return (props.flowData?.pages?.length || 0) + 1;
});

const canGoNext = computed(() => {
  return currentPageIndex.value < totalPages.value - 1;
});

const canGoPrevious = computed(() => {
  return currentPageIndex.value > 0;
});

const goToNextPage = () => {
  if (canGoNext.value) {
    currentPageIndex.value += 1;
  }
};

const goToPreviousPage = () => {
  if (canGoPrevious.value) {
    currentPageIndex.value -= 1;
  }
};

const getButtonTitle = button => {
  return button[props.buttonTitleField] || button.title || button.text;
};
</script>

<template>
  <div class="flex flex-col gap-3 order-1 lg:order-2 items-center">
    <h4 class="text-sm font-medium text-n-slate-12 w-full text-center">
      {{ title }}
    </h4>
    <div
      class="relative w-full max-w-[280px] bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-4 shadow-xl border-4 border-slate-700 aspect-phone"
    >
      <div class="flex flex-col h-full">
        <!-- Phone header -->
        <div class="flex items-center justify-center mb-3">
          <div class="w-12 h-1 rounded-full bg-slate-600" />
        </div>

        <div
          class="flex items-center gap-2 mb-4 pb-2 border-b border-slate-700"
        >
          <div
            class="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center"
          >
            <i class="i-lucide-user text-xs text-slate-300" />
          </div>
          <span class="text-xs font-medium text-slate-300">
            {{ t(`${i18nPrefix}.PREVIEW.PHONE_NUMBER_LABEL`) }}
          </span>
        </div>

        <!-- Message preview -->
        <div class="flex flex-col gap-2 flex-1 overflow-y-auto">
          <!-- Regular template preview (non-Flow) -->
          <template v-if="!isFlow">
            <div
              class="bg-white rounded-2xl rounded-bl-sm p-2.5 shadow-sm max-w-[85%]"
            >
              <!-- Media preview -->
              <div v-if="mediaUrl" class="mb-2">
                <div
                  v-if="mediaLoadError"
                  class="flex items-start gap-2 p-2 bg-n-red/10 rounded-lg border border-n-red/20"
                >
                  <i
                    class="i-lucide-alert-triangle text-xs text-n-red mt-0.5 flex-shrink-0"
                  />
                  <span class="text-[10px] text-n-red">{{
                    t(`${i18nPrefix}.PREVIEW.MEDIA_LOAD_ERROR`)
                  }}</span>
                </div>
                <img
                  v-else-if="mediaType === 'image'"
                  :src="mediaUrl"
                  alt="Media preview"
                  class="w-full h-auto rounded-lg"
                  @error="handleMediaError"
                />
                <video
                  v-else-if="mediaType === 'video'"
                  :src="mediaUrl"
                  controls
                  class="w-full h-auto rounded-lg"
                  @error="handleMediaError"
                >
                  {{ t(`${i18nPrefix}.PREVIEW.VIDEO_NOT_SUPPORTED`) }}
                </video>
                <div
                  v-else-if="mediaType === 'document'"
                  class="flex items-center gap-2 p-2 bg-slate-100 rounded-lg"
                >
                  <i class="i-lucide-file-text text-base text-slate-600" />
                  <span class="text-[10px] text-slate-700 truncate">
                    {{ t(`${i18nPrefix}.PREVIEW.DOCUMENT_LABEL`) }}
                  </span>
                </div>
              </div>

              <!-- Message text -->
              <p
                v-if="bodyMessage || !mediaUrl"
                class="text-xs text-slate-900 whitespace-pre-wrap break-words"
              >
                {{ bodyMessage || previewPlaceholder }}
              </p>

              <!-- Footer text -->
              <p
                v-if="footerText"
                class="text-[10px] text-slate-500 mt-2 pt-1 border-t border-slate-100"
              >
                {{ footerText }}
              </p>
            </div>

            <!-- Buttons preview -->
            <div class="flex flex-col gap-1.5">
              <!-- Quick Reply buttons -->
              <template v-if="buttonType === 'quick_reply'">
                <button
                  v-for="(button, index) in buttons"
                  :key="button.id || `button-${index}`"
                  type="button"
                  class="bg-white border-2 border-gray-300 rounded-lg px-3 py-2 text-xs font-bold text-center shadow-sm text-[#374151]"
                  disabled
                >
                  {{
                    getButtonTitle(button) ||
                    t(`${i18nPrefix}.PREVIEW.BUTTON_PLACEHOLDER`, {
                      number: index + 1,
                    })
                  }}
                </button>
              </template>

              <!-- Call to Action buttons -->
              <template v-if="buttonType === 'call_to_action'">
                <button
                  v-for="(button, index) in buttons"
                  :key="`button-${index}`"
                  type="button"
                  class="flex items-center justify-center gap-2 bg-white border-2 border-gray-300 rounded-lg px-3 py-2 text-xs font-bold text-center shadow-sm text-[#374151]"
                  disabled
                >
                  <i :class="getButtonIcon(button.type)" class="text-sm" />
                  <span>{{
                    getButtonTitle(button) ||
                    t(`${i18nPrefix}.PREVIEW.BUTTON_PLACEHOLDER`, {
                      number: index + 1,
                    })
                  }}</span>
                </button>
              </template>
            </div>
          </template>

          <!-- Flow template preview -->
          <template v-else>
            <div
              v-if="currentPageIndex === 0 && bodyMessage"
              class="bg-white rounded-2xl rounded-bl-sm p-2.5 shadow-sm max-w-[85%] mb-2"
            >
              <div v-if="flowData?.media_url" class="mb-2">
                <img
                  :src="flowData.media_url"
                  alt="Flow media"
                  class="w-full h-auto rounded-lg"
                />
              </div>
              <p class="text-xs text-slate-900 whitespace-pre-wrap break-words">
                {{ bodyMessage }}
              </p>
              <p
                v-if="flowData?.subtitle"
                class="text-[10px] text-slate-600 mt-1"
              >
                {{ flowData.subtitle }}
              </p>
            </div>

            <button
              v-if="currentPageIndex === 0"
              type="button"
              class="text-white rounded-lg px-3 py-2 text-xs font-bold text-center shadow-sm bg-[#2781f6]"
              @click.prevent.stop="goToNextPage"
            >
              {{
                flowData?.button_text ||
                t(`${i18nPrefix}.PREVIEW.FLOW.START_BUTTON`)
              }}
            </button>

            <div
              v-if="currentPage && currentPageIndex > 0"
              class="bg-white rounded-2xl rounded-bl-sm p-2.5 shadow-sm w-full"
            >
              <h3
                v-if="currentPage.title"
                class="text-xs font-bold text-slate-900 mb-1"
              >
                {{ currentPage.title }}
              </h3>
              <p
                v-if="currentPage.subtitle"
                class="text-[10px] text-slate-600 mb-2"
              >
                {{ currentPage.subtitle }}
              </p>
              <div class="flex flex-col gap-2 mt-2">
                <div
                  v-for="(component, index) in currentPage.components"
                  :key="`comp-${index}`"
                  class="text-xs"
                >
                  <!-- SHORT_TEXT Input -->
                  <div
                    v-if="component.type === 'SHORT_TEXT'"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-[10px] font-medium text-slate-700">
                      <span>{{ component.label }}</span>
                      <template v-if="component.required"
                        ><span class="text-red-500">*</span></template
                      >
                    </label>
                    <input
                      type="text"
                      :placeholder="component.text"
                      class="text-[10px] px-2 py-1.5 border border-slate-300 rounded bg-white"
                      disabled
                    />
                  </div>

                  <!-- LONG_TEXT Textarea -->
                  <div
                    v-else-if="component.type === 'LONG_TEXT'"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-[10px] font-medium text-slate-700">
                      <span>{{ component.label }}</span>
                      <template v-if="component.required"
                        ><span class="text-red-500">*</span></template
                      >
                    </label>
                    <textarea
                      :placeholder="component.text"
                      rows="3"
                      class="text-[10px] px-2 py-1.5 border border-slate-300 rounded bg-white resize-none"
                      disabled
                    />
                  </div>

                  <!-- SINGLE_SELECT Radio buttons -->
                  <div
                    v-else-if="component.type === 'SINGLE_SELECT'"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-[10px] font-medium text-slate-700">
                      <span>{{ component.label }}</span>
                      <template v-if="component.required"
                        ><span class="text-red-500">*</span></template
                      >
                    </label>
                    <div class="flex flex-col gap-1">
                      <label
                        v-for="(option, optIndex) in component.options || []"
                        :key="`opt-${optIndex}`"
                        class="flex items-center gap-1.5"
                      >
                        <input
                          type="radio"
                          :name="`single-${index}`"
                          class="w-3 h-3"
                          disabled
                        />
                        <span class="text-[10px] text-slate-700">{{
                          option.title
                        }}</span>
                      </label>
                    </div>
                  </div>

                  <!-- MULTI_SELECT Checkboxes -->
                  <div
                    v-else-if="component.type === 'MULTI_SELECT'"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-[10px] font-medium text-slate-700">
                      <span>{{ component.label }}</span>
                      <template v-if="component.required"
                        ><span class="text-red-500">*</span></template
                      >
                    </label>
                    <div class="flex flex-col gap-1">
                      <label
                        v-for="(option, optIndex) in component.options || []"
                        :key="`opt-${optIndex}`"
                        class="flex items-center gap-1.5"
                      >
                        <input type="checkbox" class="w-3 h-3" disabled />
                        <span class="text-[10px] text-slate-700">{{
                          option.title
                        }}</span>
                      </label>
                    </div>
                  </div>

                  <!-- LIST Dropdown -->
                  <div
                    v-else-if="component.type === 'LIST'"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-[10px] font-medium text-slate-700">
                      <span>{{ component.label }}</span>
                      <template v-if="component.required"
                        ><span class="text-red-500">*</span></template
                      >
                    </label>
                    <select
                      class="text-[10px] px-2 py-1.5 border border-slate-300 rounded bg-white"
                      disabled
                    >
                      <option value="">
                        {{ t(`${i18nPrefix}.PREVIEW.FLOW.SELECT_OPTION`) }}
                      </option>
                      <option
                        v-for="(option, optIndex) in component.options || []"
                        :key="`opt-${optIndex}`"
                        :value="option.id"
                      >
                        {{ option.title }}
                      </option>
                    </select>
                  </div>

                  <!-- DATE_PICKER -->
                  <div
                    v-else-if="component.type === 'DATE_PICKER'"
                    class="flex flex-col gap-1"
                  >
                    <label class="text-[10px] font-medium text-slate-700">
                      <span>{{ component.label }}</span>
                      <template v-if="component.required"
                        ><span class="text-red-500">*</span></template
                      >
                    </label>
                    <input
                      type="date"
                      class="text-[10px] px-2 py-1.5 border border-slate-300 rounded bg-white"
                      :min="component.min_date"
                      :max="component.max_date"
                      disabled
                    />
                  </div>

                  <!-- Text display components -->
                  <div
                    v-else-if="component.type === 'TEXT_HEADING'"
                    class="mt-1"
                  >
                    <h4 class="text-xs font-bold text-slate-900">
                      {{ component.text }}
                    </h4>
                  </div>
                  <div
                    v-else-if="component.type === 'TEXT_SUBHEADING'"
                    class="mt-1"
                  >
                    <h5 class="text-[11px] font-semibold text-slate-800">
                      {{ component.text }}
                    </h5>
                  </div>
                  <div v-else-if="component.type === 'TEXT_BODY'" class="mt-1">
                    <p class="text-[10px] text-slate-700">
                      {{ component.text }}
                    </p>
                  </div>
                  <div
                    v-else-if="component.type === 'TEXT_CAPTION'"
                    class="mt-1"
                  >
                    <p class="text-[9px] text-slate-500 italic">
                      {{ component.text }}
                    </p>
                  </div>
                  <div v-else-if="component.type === 'RICH_TEXT'" class="mt-1">
                    <div class="text-[10px] text-slate-700 whitespace-pre-wrap">
                      {{ component.text }}
                    </div>
                  </div>

                  <!-- MEDIA -->
                  <div v-else-if="component.type === 'MEDIA'" class="mt-1">
                    <img
                      v-if="component.media_url"
                      :src="component.media_url"
                      alt="Component media"
                      class="w-full h-auto rounded"
                    />
                    <div
                      v-else
                      class="flex items-center justify-center p-4 bg-slate-100 rounded"
                    >
                      <i class="i-lucide-image text-base text-slate-400" />
                    </div>
                  </div>

                  <!-- FOOTER -->
                  <button
                    v-else-if="component.type === 'FOOTER'"
                    type="button"
                    class="w-full mt-2 bg-blue-500 text-white rounded px-3 py-1.5 text-[10px] font-bold"
                    disabled
                  >
                    {{
                      component.label ||
                      t(`${i18nPrefix}.PREVIEW.FLOW.CONTINUE`)
                    }}
                  </button>

                  <!-- Unknown component type fallback -->
                  <div
                    v-else
                    class="p-1.5 bg-slate-50 rounded border border-slate-200"
                  >
                    <span class="text-[10px] text-slate-500">
                      {{ component.type }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Flow navigation -->
            <div
              v-if="totalPages > 1"
              class="flex items-center justify-between gap-2 mt-2"
            >
              <button
                v-if="currentPageIndex > 0"
                type="button"
                class="flex items-center gap-1 px-2 py-1 text-[10px] rounded disabled:opacity-50 text-white bg-[#696e771a]"
                :disabled="!canGoPrevious"
                @click.prevent.stop="goToPreviousPage"
              >
                <i class="i-lucide-chevron-left text-xs" />
                {{ t(`${i18nPrefix}.PREVIEW.FLOW.PREVIOUS`) }}
              </button>
              <div v-else />

              <span class="text-[10px] text-slate-300">
                {{
                  currentPageIndex === 0
                    ? t(`${i18nPrefix}.PREVIEW.FLOW.START`)
                    : `${currentPageIndex}/${flowData?.pages?.length || 0}`
                }}
              </span>

              <button
                v-if="currentPageIndex === 0"
                type="button"
                class="flex items-center gap-1 px-2 py-1 text-[10px] text-white rounded bg-[#2781f6]"
                @click.prevent.stop="goToNextPage"
              >
                {{ t(`${i18nPrefix}.PREVIEW.FLOW.NEXT`) }}
                <i class="i-lucide-chevron-right text-xs" />
              </button>
              <button
                v-else
                type="button"
                class="flex items-center gap-1 px-2 py-1 text-[10px] text-white rounded disabled:opacity-50 bg-[#2781f6]"
                :disabled="!canGoNext"
                @click.prevent.stop="goToNextPage"
              >
                {{ t(`${i18nPrefix}.PREVIEW.FLOW.NEXT`) }}
                <i class="i-lucide-chevron-right text-xs" />
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
